/**
 * Field Default Configurations Index
 *
 * Exports all field type default configuration factories.
 */

// Import all config factories for the factory map
import { createTextConfig } from './text.config';
import { createIntegerConfig } from './integer.config';
import { createDecimalConfig } from './decimal.config';
import { createFractionConfig } from './fraction.config';
import { createRangeConfig } from './range.config';
import { createBooleanConfig } from './boolean.config';
import { createMultiChoiceConfig } from './multichoice.config';
import { createCategoryConfig } from './category.config';
import { createConnectorConfig } from './connector.config';

// Text Field
export {
  createTextConfig,
  TEXT_DEFAULT_CONFIG,
  default as createTextConfigDefault,
  type TextFieldConfig,
} from './text.config';

// Integer Field
export {
  createIntegerConfig,
  INTEGER_DEFAULT_CONFIG,
  default as createIntegerConfigDefault,
  type IntegerFieldConfig,
} from './integer.config';

// Decimal Field
export {
  createDecimalConfig,
  DECIMAL_DEFAULT_CONFIG,
  default as createDecimalConfigDefault,
  type DecimalFieldConfig,
} from './decimal.config';

// Fraction Field
export {
  createFractionConfig,
  FRACTION_DEFAULT_CONFIG,
  default as createFractionConfigDefault,
  type FractionFieldConfig,
} from './fraction.config';

// Range Field
export {
  createRangeConfig,
  RANGE_DEFAULT_CONFIG,
  default as createRangeConfigDefault,
  type RangeFieldConfig,
} from './range.config';

// Boolean Field
export {
  createBooleanConfig,
  BOOLEAN_DEFAULT_CONFIG,
  default as createBooleanConfigDefault,
  type BooleanFieldConfig,
} from './boolean.config';

// MultiChoice Field
export {
  createMultiChoiceConfig,
  createMultiChoiceConfigForStyle,
  MULTICHOICE_DEFAULT_CONFIG,
  getStyleDefaults as getMultiChoiceStyleDefaults,
  default as createMultiChoiceConfigDefault,
  type MultiChoiceFieldConfig,
  type MultiChoiceStyle,
  type ChipSize,
  type ImageDisplay as MultiChoiceImageDisplay,
  type LayoutStyle,
  type TextAlignment as MultiChoiceTextAlignment,
  type ColumnSelection,
  type LayoutDirection,
  type ChipWidth,
  type ChipLabelPosition,
  type LoadOption,
  type LoadRemainingOption,
  type ExcludedOption,
  type Choice,
  type InfoDisplay,
  type InfoTrigger,
  type InfoSize,
  type InfoForOptionsType,
} from './multichoice.config';

// Category Field
export {
  createCategoryConfig,
  createCategoryConfigForStyle,
  CATEGORY_DEFAULT_CONFIG,
  getStyleDefaults as getCategoryStyleDefaults,
  default as createCategoryConfigDefault,
  type CategoryFieldConfig,
  type CategoryStyle,
  type ImageDisplay as CategoryImageDisplay,
  type ProductsLoading,
  type DescriptionStyle,
  type TextAlignment as CategoryTextAlignment,
  type PdpLaunchIn,
} from './category.config';

// Connector Field
export {
  createConnectorConfig,
  CONNECTOR_DEFAULT_CONFIG,
  default as createConnectorConfigDefault,
  type ConnectorFieldConfig,
} from './connector.config';

// ============================================================================
// Factory Map
// ============================================================================

// Type alias for config factory functions
type ConfigFactory = (fieldName: string) => Record<string, unknown>;

/**
 * Map of field type IDs to their config factory functions
 * Includes aliases for hyphenated variants (e.g., 'multi-choice' → createMultiChoiceConfig)
 */
export const fieldConfigFactories: Record<string, ConfigFactory> = {
  text: createTextConfig as unknown as ConfigFactory,
  integer: createIntegerConfig as unknown as ConfigFactory,
  decimal: createDecimalConfig as unknown as ConfigFactory,
  fraction: createFractionConfig as unknown as ConfigFactory,
  range: createRangeConfig as unknown as ConfigFactory,
  boolean: createBooleanConfig as unknown as ConfigFactory,
  multichoice: createMultiChoiceConfig as unknown as ConfigFactory,
  'multi-choice': createMultiChoiceConfig as unknown as ConfigFactory, // Alias for API variant
  category: createCategoryConfig as unknown as ConfigFactory,
  connector: createConnectorConfig as unknown as ConfigFactory,
};

/**
 * Get the default configuration factory for a field type
 * @param fieldType The field type ID
 * @returns The configuration factory function or undefined
 */
export const getFieldConfigFactory = (
  fieldType: string
): ((fieldName: string) => Record<string, unknown>) | undefined => {
  return fieldConfigFactories[fieldType];
};

/**
 * Create default configuration for any field type
 * @param fieldType The field type ID (handles variations like "Multi-choice")
 * @param fieldName The field name
 * @returns The default configuration or a basic config if type not found
 */
export const createFieldConfig = (
  fieldType: string,
  fieldName: string
): Record<string, unknown> => {
  // Normalize field type to handle variations like "Multi-choice" → "multichoice"
  const normalizedType = fieldType.toLowerCase().replace(/-/g, '');
  const factory = fieldConfigFactories[normalizedType];
  if (factory) {
    return factory(fieldName);
  }
  // Fallback for unknown field types
  return { label: fieldName, showTitle: true };
};
